import React from 'react';

// Creator-written text (course and event descriptions) rendered with the little
// bit of formatting people actually paste: links, bold, bullet lists, headings,
// and blank lines between paragraphs. Markdown or the matching plain HTML tags
// both work; everything else shows as the text it is. Nothing is injected as
// HTML, so a pasted <script> is just letters on the page.
//
//   [text](https://…)  or  <a href="https://…">text</a>  or a bare https:// link
//   **bold**  or  <b>/<strong>        *italic*  or  <i>/<em>
//   - item / * item / • item            ## Heading
//   a blank line starts a new paragraph; a single line break stays a line break
//
// The output owns its own spacing (paragraphs, lists, headings), so put it in a
// plain <div>, not in a pre-line <p>. Plain text with none of the above comes back
// as one pre-line block, so old descriptions look exactly as they did.

const LINK_STYLE: React.CSSProperties = { color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3 };
const P: React.CSSProperties = { margin: '0 0 12px' };

const safeHref = (raw: string) => {
  const s = raw.trim();
  if (/^(mailto:|tel:)/i.test(s)) return s;
  if (/^www\./i.test(s)) return `https://${s}`;
  return /^https?:\/\//i.test(s) ? s : null;
};

// Turn the HTML tags we accept into their markdown spelling, then drop every other tag.
function normalize(text: string) {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/<a\b[^>]*href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi, (_, href, label) => `[${label.replace(/<[^>]+>/g, '').trim() || href}](${href})`)
    .replace(/<(strong|b)\b[^>]*>([\s\S]*?)<\/\1>/gi, '**$2**')
    .replace(/<(em|i)\b[^>]*>([\s\S]*?)<\/\1>/gi, '*$2*')
    .replace(/<h[1-6]\b[^>]*>([\s\S]*?)<\/h[1-6]>/gi, '\n\n## $1\n\n')
    .replace(/<li\b[^>]*>([\s\S]*?)<\/li>/gi, '\n- $1')
    .replace(/<\/(p|div|ul|ol|blockquote)>/gi, '\n\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, '\'');
}

const RICH = /\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*|(?<![*\w])\*[^*\n]+\*(?![*\w])|(?:https?:\/\/|www\.)\S+|^\s*[-*•]\s+\S|^\s*#{1,6}\s+\S/m;

// Inline: links, bold, italic, inside one line.
function inline(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|(?<![*\w])\*([^*\n]+)\*(?![*\w])|((?:https?:\/\/|www\.)[^\s<>()"'\]]+[^\s<>()"'\].,;:!?])/gi;
  let last = 0; let m: RegExpExecArray | null; let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(<React.Fragment key={`${keyBase}t${i++}`}>{text.slice(last, m.index)}</React.Fragment>);
    if (m[1] !== undefined) {
      const href = safeHref(m[2]);
      out.push(href ? <a key={`${keyBase}a${i++}`} href={href} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>{inline(m[1], `${keyBase}a${i}`)}</a> : <React.Fragment key={`${keyBase}a${i++}`}>{m[1]}</React.Fragment>);
    } else if (m[3] !== undefined) out.push(<strong key={`${keyBase}b${i++}`}>{inline(m[3], `${keyBase}b${i}`)}</strong>);
    else if (m[4] !== undefined) out.push(<em key={`${keyBase}e${i++}`}>{m[4]}</em>);
    else if (m[5] !== undefined) { const href = safeHref(m[5]); out.push(href ? <a key={`${keyBase}u${i++}`} href={href} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>{m[5]}</a> : m[5]); }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(<React.Fragment key={`${keyBase}t${i++}`}>{text.slice(last)}</React.Fragment>);
  return out;
}

// Lines of one paragraph, with the single line breaks kept.
const withBreaks = (lines: string[], keyBase: string) => lines.flatMap((l, i) => (i ? [<br key={`${keyBase}br${i}`} />, ...inline(l, `${keyBase}l${i}`)] : inline(l, `${keyBase}l${i}`)));

export function renderRichText(raw: string, opts: { headingSize?: number; inlineOnly?: boolean } = {}): React.ReactNode {
  const text = normalize(String(raw || '')).trim();
  if (!text) return null;
  // Inline only (a short blurb inside a span): links and emphasis, line breaks kept, no blocks.
  if (opts.inlineOnly) return <>{withBreaks(text.split('\n').map((l) => l.replace(/^\s*[-*•]\s+/, '').replace(/^\s*#{1,6}\s+/, '')), 'i')}</>;
  // Plain text: one block, line breaks as typed, exactly as before.
  if (!RICH.test(text)) return <span style={{ whiteSpace: 'pre-line' }}>{text}</span>;

  const blocks: React.ReactNode[] = [];
  let para: string[] = []; let list: React.ReactNode[] | null = null; let k = 0;
  const endPara = () => { if (para.length) { blocks.push(<p key={`p${k++}`} style={P}>{withBreaks(para, `p${k}`)}</p>); para = []; } };
  const endList = () => { if (list) { blocks.push(<ul key={`l${k++}`} style={{ margin: '0 0 12px', paddingLeft: 22, listStyle: 'disc' }}>{list}</ul>); list = null; } };
  for (const line of text.split('\n')) {
    const li = line.match(/^\s*[-*•]\s+(.*)$/);
    if (li) { endPara(); (list ||= []).push(<li key={`i${k++}`} style={{ margin: '0 0 4px', display: 'list-item' }}>{inline(li[1], `i${k}`)}</li>); continue; }
    endList();
    const h = line.match(/^\s*#{1,6}\s+(.*)$/);
    if (h) { endPara(); blocks.push(<strong key={`h${k++}`} style={{ display: 'block', fontSize: opts.headingSize || 16, margin: '18px 0 6px' }}>{inline(h[1], `h${k}`)}</strong>); continue; }
    if (!line.trim()) { endPara(); continue; }
    para.push(line);
  }
  endPara(); endList();
  // The last block carries no bottom margin, so the panel below sits where it did.
  return <div className="hj-rich" style={{ marginBottom: -12 }}>{blocks}</div>;
}

export default renderRichText;
